/**
 * The built-in copywriting instruction, offered as a **prefill** rather than as stored text.
 *
 * The suggestion for a list's source type (`SUGGESTED_COPYWRITING_INSTRUCTIONS`) is shown in the
 * empty instruction box the way a filled-in form field looks, but nothing is persisted until the
 * user adopts it by clicking into the text. The alternative — writing it to the list at creation —
 * would put words in the agents' mouths that nobody chose, and would leave every list already in
 * the database without them.
 *
 * Dismissing it is remembered in `localStorage` per list: it is a per-person "no thanks", not a
 * setting the whole company shares, and it must survive a reload or the X is pointless.
 */

const DISMISSED_KEY_PREFIX = "outreach-instruction-prefill-dismissed";

/** The `localStorage` key holding one list's dismissal. */
export function instructionPrefillDismissKey(listId: number): string {
	return `${DISMISSED_KEY_PREFIX}:${listId}`;
}

/**
 * Whether this browser has dismissed the prefill for this list.
 *
 * Storage access is guarded: Safari's private mode and a blocked-cookies profile throw on read as
 * well as on write, and a prefill is not worth crashing the step over — an unreadable store simply
 * means "not dismissed".
 */
export function isInstructionPrefillDismissed(listId: number): boolean {
	try {
		return window.localStorage.getItem(instructionPrefillDismissKey(listId)) !== null;
	} catch {
		return false;
	}
}

/** Remember that this browser dismissed the prefill for this list. */
export function dismissInstructionPrefill(listId: number): void {
	try {
		window.localStorage.setItem(instructionPrefillDismissKey(listId), "1");
	} catch {
		// A browser that refuses the write still gets the dismissal for this visit — the component
		// holds it in state — it just cannot remember it for the next one.
	}
}

/** What the editor needs to know to decide whether the prefill is on screen. */
export interface InstructionPrefillState {
	/** The suggested body, or undefined when the source type has none / it was dismissed. */
	prefillText?: string;
	/** The instruction as stored on the list. */
	value: string;
	/** True while the user is typing in the box. */
	isFocused: boolean;
}

/**
 * Whether the prefill is shown in place of the empty box.
 *
 * Only over a genuinely empty instruction: any stored text — typed, loaded from a template, or
 * adopted from this very prefill — is the user's, and must never be covered by a suggestion. It
 * also steps aside while the box has focus, so a user who tabbed in sees the field they are about
 * to type into rather than text they did not write.
 */
export function isInstructionPrefillVisible({
	prefillText,
	value,
	isFocused,
}: InstructionPrefillState): boolean {
	if (!prefillText || isFocused) {
		return false;
	}

	return value.trim() === "";
}
